import api from '../httpClient';
import type { AppointmentAvailableSlot, AppointmentAvailableSlotsResponse } from './appointments';

export interface PublicNutritionistInfo {
  id: string;
  name: string;
  email?: string;
  profileImageUrl?: string;
  timezone: string;
}

export interface PublicClientInfo {
  id: string;
  name: string;
  email?: string;
}

export interface PublicTimeOff {
  startDate: string;
  endDate: string;
}

export interface PublicScheduleDay {
  day: string;
  isEnabled: boolean;
  slots: { startTime: string; endTime: string }[];
}

export interface PublicAppointmentInfo {
  id?: string;
  title?: string;
  startTime?: string;
  endTime?: string;
  appointmentType?: 'online' | 'offline';
}

export interface PublicBookingLinkInfo {
  linkId: string;
  expiresAt: string;
  nutritionist: PublicNutritionistInfo;
  client?: PublicClientInfo;
  timeOff?: PublicTimeOff[];
  schedule?: {
    days: PublicScheduleDay[];
  };
  appointment?: PublicAppointmentInfo;
}

export interface GetBookingLinkInfoResponse {
  data: PublicBookingLinkInfo;
}

export const getBookingLinkInfo = async (linkId: string): Promise<GetBookingLinkInfoResponse> => {
  const response = await api.get(`/appointments-public/booking-links/${linkId}`);
  return response.data;
};

const unwrapEnvelope = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: unknown }).data;
  }
  return payload;
};

/** Normalizes available-slots responses from public booking/reschedule endpoints. */
export const parsePublicAvailableSlotsResponse = (
  payload: unknown,
  fallback: { date: string; duration: number; timezone?: string },
): AppointmentAvailableSlotsResponse => {
  const root = unwrapEnvelope(payload);
  const body = root && typeof root === 'object' ? (root as Record<string, unknown>) : {};
  const slots = Array.isArray(body.availableSlots)
    ? body.availableSlots
    : Array.isArray(body.slots)
      ? body.slots
      : [];

  const scheduleTimezone = String(
    body.scheduleTimezone ?? body.timezone ?? fallback.timezone ?? 'UTC',
  );
  const displayTimezone = String(body.timezone ?? fallback.timezone ?? scheduleTimezone);

  return {
    date: String(body.date ?? fallback.date),
    scheduleTimezone,
    timezone: displayTimezone,
    duration: Number(body.duration ?? fallback.duration),
    availableSlots: slots
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const slot = item as Record<string, unknown>;
        const startTime = String(slot.startTime ?? slot.start ?? slot.time ?? '').trim();
        const endTime = String(slot.endTime ?? slot.end ?? '').trim();
        const startTimeUtc = String(slot.startTimeUtc ?? '').trim();
        const endTimeUtc = String(slot.endTimeUtc ?? '').trim();
        if (!startTime) return null;
        return {
          startTime,
          endTime: endTime || startTime,
          startTimeUtc,
          endTimeUtc,
        } satisfies AppointmentAvailableSlot;
      })
      .filter((slot): slot is AppointmentAvailableSlot => Boolean(slot)),
  };
};

export const getAvailableSlots = async (
  linkId: string,
  date: string,
  duration: number,
  timezone?: string,
): Promise<AppointmentAvailableSlotsResponse> => {
  const params = new URLSearchParams({ date, duration: String(duration) });
  if (timezone) {
    params.set('timezone', timezone);
  }
  const response = await api.get(
    `/appointments-public/booking-links/${linkId}/available-slots?${params.toString()}`,
  );
  return parsePublicAvailableSlotsResponse(response.data, { date, duration, timezone });
};

export interface BookAppointmentPayload {
  title: string;
  appointmentType: 'online' | 'offline';
  /** Option A — full UTC ISO instants (preferred) */
  startTime?: string;
  endTime?: string;
  /** Option B — wall-clock date + times + timezone */
  date?: string;
  timezone?: string;
  clientId?: string;
  clientName?: string;
  clientEmail?: string;
  additionalAttendees?: string[];
}

export interface BookAppointmentResponse {
  message: string;
  data: {
    id: string;
    [key: string]: unknown;
  };
}

export const bookAppointment = async (
  linkId: string,
  payload: BookAppointmentPayload,
): Promise<BookAppointmentResponse> => {
  const response = await api.post(`/appointments-public/booking-links/${linkId}/book`, payload);
  return response.data;
};

// Reschedule API Endpoints

export const getRescheduleLinkInfo = async (linkId: string): Promise<GetBookingLinkInfoResponse> => {
  const response = await api.get(`/appointments-public/reschedule-links/${linkId}`);
  return response.data;
};

export const getRescheduleAvailableSlots = async (
  linkId: string,
  date: string,
  duration: number,
  timezone?: string,
): Promise<AppointmentAvailableSlotsResponse> => {
  const params = new URLSearchParams({ date, duration: String(duration) });
  if (timezone) {
    params.set('timezone', timezone);
  }
  const response = await api.get(
    `/appointments-public/reschedule-links/${linkId}/available-slots?${params.toString()}`,
  );
  return parsePublicAvailableSlotsResponse(response.data, { date, duration, timezone });
};

export const bookRescheduleAppointment = async (
  linkId: string,
  payload: BookAppointmentPayload,
): Promise<BookAppointmentResponse> => {
  const response = await api.post(`/appointments-public/reschedule-links/${linkId}/book`, payload);
  return response.data;
};
